import queryClient from '@src/app/providers/queryClient';
import type { APIClient } from '@src/shared/api';
import { formatIsoDate, subtractDay } from '@src/shared/lib';
import { useMutation, useQuery } from '@src/shared/query';
import { urlToQueryKey } from '@src/shared/query/utils';
import { selectAbsences } from '../../lib/absence';
import type { LoadedRange } from '../../lib/calendar';
import type { Absence, AbsenceDto } from '../../types';
import {
  absenceApproveHrUrl,
  absenceApproveUrl,
  absenceDeleteUrl,
  absenceDenyUrl,
  absenceRequestUrl,
  absenceUrl,
  absenceWithdrawApproveUrl,
  absenceWithdrawDenyUrl,
  absenceWithdrawUrl,
  groupStatsUrl,
  vacationBalanceUrl,
} from './constants';

export function invalidateAbsenceQueries() {
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: ['root', ...urlToQueryKey(absenceUrl)],
    }),
    queryClient.invalidateQueries({
      queryKey: ['root', ...urlToQueryKey(groupStatsUrl)],
    }),
    queryClient.invalidateQueries({
      queryKey: ['root', ...urlToQueryKey(vacationBalanceUrl)],
    }),
  ]);
}

export type AbsenceQueryParams = Partial<{
  date_from: string;
  date_to: string;
  user_id: string;
}>;

type AbsenceQueryRange = {
  start: Date;
  end: Date;
};

/**
 * The days the calendar asks for, both ends inclusive.
 *
 * A day before what is drawn, so the absences at the edges of the calendar
 * can be told whether they carry on -- which is what decides the shape they
 * are drawn with, and whether an entry may be reshaped from what is on
 * screen. Anything checking against the rows in hand has to measure against
 * this rather than against the drawn range.
 */
export function getLoadedRange({ start, end }: AbsenceQueryRange): LoadedRange {
  return { from: formatIsoDate(subtractDay(start, 1)), to: formatIsoDate(end) };
}

export function getAbsenceQueryParams(
  range: AbsenceQueryRange,
): AbsenceQueryParams {
  const { from, to } = getLoadedRange(range);
  return { date_from: from, date_to: to };
}

export default function useAbsences(queryParams: AbsenceQueryParams = {}) {
  const query = useQuery<AbsenceDto[], Absence[]>(absenceUrl, {
    queryParams,
    select: selectAbsences,
    initialData: [],
    placeholderData: (previousData) => previousData,
  });

  const { mutateAsync: bookAbsence } = useMutation<
    { absences: Absence[] },
    Error,
    Record<string, unknown> & { headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      headers,
      ...rest
    }: { apiClient: APIClient } & Record<string, unknown>) => {
      return apiClient.request({
        url: absenceUrl,
        method: 'POST',
        headers: headers as Record<string, string> | undefined,
        payload: rest,
      }) as Promise<{ absences: Absence[] }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: deleteAbsences } = useMutation<
    { absences: Absence[] },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceDeleteUrl,
        method: 'DELETE',
        headers,
        payload: ids,
        bodyOnDelete: true,
      }) as Promise<{ absences: Absence[] }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: requestAbsences } = useMutation<
    { requested: number },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceRequestUrl,
        method: 'POST',
        headers,
        payload: { ids },
      }) as Promise<{ requested: number }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: withdrawAbsences } = useMutation<
    { withdrawn: number; requested: number },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceWithdrawUrl,
        method: 'POST',
        headers,
        payload: { ids },
      }) as Promise<{ withdrawn: number; requested: number }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: approveWithdrawalAbsences } = useMutation<
    { withdrawn: number; forwarded: number },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceWithdrawApproveUrl,
        method: 'POST',
        headers,
        payload: { ids },
      }) as Promise<{ withdrawn: number; forwarded: number }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: denyWithdrawalAbsences } = useMutation<
    { denied: number },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceWithdrawDenyUrl,
        method: 'POST',
        headers,
        payload: { ids },
      }) as Promise<{ denied: number }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: approveAbsences } = useMutation<
    { approved: number },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceApproveUrl,
        method: 'POST',
        headers,
        payload: { ids },
      }) as Promise<{ approved: number }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: approveHrAbsences } = useMutation<
    { approved: number },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceApproveHrUrl,
        method: 'POST',
        headers,
        payload: { ids },
      }) as Promise<{ approved: number }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { mutateAsync: denyAbsences } = useMutation<
    { denied: number },
    Error,
    { ids: number[]; headers?: Record<string, string> }
  >({
    mutateFn: ({
      apiClient,
      ids,
      headers,
    }: {
      apiClient: APIClient;
      ids: number[];
      headers?: Record<string, string>;
    }) => {
      return apiClient.request({
        url: absenceDenyUrl,
        method: 'POST',
        headers,
        payload: { ids },
      }) as Promise<{ denied: number }>;
    },
    onSuccess: invalidateAbsenceQueries,
  });

  const { data: absences = [], ...restQuery } = query;
  return {
    absences,
    bookAbsence,
    deleteAbsences,
    requestAbsences,
    withdrawAbsences,
    approveWithdrawalAbsences,
    denyWithdrawalAbsences,
    approveAbsences,
    approveHrAbsences,
    denyAbsences,
    ...restQuery,
  };
}
